import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { ManualLoader } from "src/components";
import {
  useGetRenewalNtvQuery,
  useGetUserQuery,
  useSubmitRenewalNtvMutation,
} from "src/store/services/privateApi";
import { getCurrentLease } from "src/helpers/helperLease";
import tokens from "src/shared/theme/tokens";
import {
  RenewalWizardShell,
  renewalWizardFooterSx,
} from "./RenewalWizardShell";
import {
  clearNtvDraft,
  loadNtvDraft,
  minMoveOutDateIso,
  saveNtvDraft,
  validateNtvMoveOutDate,
  type NtvDraft,
} from "./ntvValidate";

type Step = "summary" | "details" | "review" | "done";

const formatDisplayDate = (iso: string | null | undefined) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    const local = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (local) {
      return new Date(
        Number(local[1]),
        Number(local[2]) - 1,
        Number(local[3])
      ).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }
    return iso;
  }
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const emptyDraft = (): NtvDraft => ({
  moveOutDate: "",
  allLeaving: null,
  personIds: [],
  reasonId: "",
  forwardingAddress: "",
});

const headingSx = {
  fontFamily: tokens.fonts.display,
  fontWeight: 700,
  fontSize: { xs: 22, sm: 28 },
  color: tokens.colors.primaryDeep,
  mb: 1,
} as const;

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: { xs: "column", sm: "row" },
      gap: { xs: 0.25, sm: 2 },
      py: 1.25,
      borderBottom: `1px solid ${tokens.colors.border}`,
    }}
  >
    <Typography
      sx={{
        minWidth: { sm: 160 },
        color: tokens.colors.inkMuted,
        typography: "body2",
        fontWeight: 600,
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ color: tokens.colors.ink }}>{value}</Typography>
  </Box>
);

/**
 * COPS NTV self-service: household summary → form → review → Central Ops email.
 */
export const RenewalNtvScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: userData } = useGetUserQuery(null);
  const currentLease = getCurrentLease(userData);
  const leaseId = currentLease?.id ?? "";

  const { data, isLoading, isError } = useGetRenewalNtvQuery(leaseId, {
    skip: !leaseId,
  });
  const [submitNtv, submitState] = useSubmitRenewalNtvMutation();

  const [step, setStep] = useState<Step>("summary");
  const [draft, setDraft] = useState<NtvDraft>(emptyDraft);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoading && data && !data.eligible) {
      navigate("/home", { replace: true });
    }
  }, [data, isLoading, navigate]);

  useEffect(() => {
    if (!leaseId) return;
    const saved = loadNtvDraft(leaseId);
    if (saved) setDraft(saved);
  }, [leaseId]);

  useEffect(() => {
    if (!leaseId) return;
    saveNtvDraft(leaseId, draft);
  }, [draft, leaseId]);

  const noticeDays = data?.noticeDaysRequired ?? 60;
  const minDate = useMemo(() => minMoveOutDateIso(noticeDays), [noticeDays]);
  const household = data?.household ?? [];
  const reasons = data?.reasons ?? [];
  const blackouts = data?.blackouts ?? [];

  const leavingNames = useMemo(() => {
    if (draft.allLeaving) return household.map((h) => h.name).join(", ");
    return household
      .filter((h) => draft.personIds.includes(h.id))
      .map((h) => h.name)
      .join(", ");
  }, [draft.allLeaving, draft.personIds, household]);

  const reasonLabel =
    reasons.find((r) => r.id === draft.reasonId)?.label || draft.reasonId;

  // 4 = past last step → shell marks stepper complete on done
  const activeStep =
    step === "done" ? 4 : step === "summary" ? 1 : step === "details" ? 2 : 3;

  if (!leaseId || isLoading) {
    return <ManualLoader />;
  }

  if (isError || !data?.eligible) {
    return <ManualLoader />;
  }

  const validateDetails = (): boolean => {
    const dateResult = validateNtvMoveOutDate({
      moveOutDate: draft.moveOutDate,
      noticeDays,
      blackouts,
    });
    if (dateResult === "invalid_date") {
      setFieldError(t("renewal.ntv.errInvalidDate"));
      return false;
    }
    if (dateResult === "too_soon") {
      setFieldError(t("renewal.ntv.errTooSoon", { days: noticeDays }));
      return false;
    }
    if (dateResult === "blackout") {
      setFieldError(t("renewal.ntv.errBlackout"));
      return false;
    }
    if (draft.allLeaving === null) {
      setFieldError(t("renewal.ntv.allLeaving"));
      return false;
    }
    if (draft.allLeaving === false) {
      if (draft.personIds.length === 0) {
        setFieldError(t("renewal.ntv.errWhoRequired"));
        return false;
      }
      if (household.length > 0 && draft.personIds.length >= household.length) {
        setFieldError(t("renewal.ntv.errWhoAll"));
        return false;
      }
    }
    if (!draft.reasonId) {
      setFieldError(t("renewal.ntv.errReason"));
      return false;
    }
    if (!draft.forwardingAddress.trim()) {
      setFieldError(t("renewal.ntv.errForwarding"));
      return false;
    }
    if (reasons.length === 0) {
      setFieldError(t("renewal.ntv.errReasonsEmpty"));
      return false;
    }
    setFieldError(null);
    return true;
  };

  const onSubmit = async () => {
    if (!validateDetails()) {
      setStep("details");
      return;
    }
    setSubmitError(null);
    try {
      await submitNtv({
        leaseId,
        body: {
          moveOutDate: draft.moveOutDate,
          allLeaving: draft.allLeaving === true,
          personIds: draft.allLeaving === true ? undefined : draft.personIds,
          reasonId: draft.reasonId,
          forwardingAddress: draft.forwardingAddress.trim(),
        },
      }).unwrap();
      clearNtvDraft(leaseId);
      setStep("done");
    } catch {
      setSubmitError(t("renewal.ntv.errorGeneric"));
    }
  };

  const footer =
    step === "done" ? undefined : step === "summary" ? (
      <Box sx={renewalWizardFooterSx}>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => navigate("/renewal")}
          sx={{ borderColor: tokens.colors.primary }}
        >
          {t("renewal.steps.back")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setStep("details")}
          sx={{ minWidth: 140 }}
        >
          {t("renewal.steps.continue")}
        </Button>
      </Box>
    ) : step === "details" ? (
      <Box sx={renewalWizardFooterSx}>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => setStep("summary")}
          sx={{ borderColor: tokens.colors.primary }}
        >
          {t("renewal.steps.back")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            if (validateDetails()) setStep("review");
          }}
          sx={{ minWidth: 140 }}
        >
          {t("renewal.steps.continue")}
        </Button>
      </Box>
    ) : (
      <Box sx={renewalWizardFooterSx}>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => setStep("details")}
          disabled={submitState.isLoading}
          sx={{ borderColor: tokens.colors.primary }}
        >
          {t("renewal.steps.back")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onSubmit}
          disabled={submitState.isLoading}
          sx={{ minWidth: 160 }}
        >
          {submitState.isLoading
            ? t("renewal.ntv.submitting")
            : t("renewal.ntv.submit")}
        </Button>
      </Box>
    );

  return (
    <RenewalWizardShell path="ntv" activeStep={activeStep} footer={footer}>
      {step === "summary" && (
        <>
          <Typography component="h1" sx={headingSx}>
            {t("renewal.ntv.summaryTitle")}
          </Typography>
          <Typography sx={{ mb: 3, color: tokens.colors.inkSecondary }}>
            {t("renewal.ntv.summarySubtitle")}
          </Typography>
          <Box
            sx={{
              border: `1px solid ${tokens.colors.border}`,
              borderRadius: `${tokens.radius.md}px`,
              px: 2,
              backgroundColor: tokens.colors.surface,
            }}
          >
            <SummaryRow
              label={t("renewal.ntv.leavingLabel")}
              value={household.map((h) => h.name).join(", ") || "—"}
            />
            <SummaryRow
              label={t("renewal.ntv.propertyLabel")}
              value={data.propertyName || "—"}
            />
            <SummaryRow
              label={t("renewal.ntv.unitLabel")}
              value={data.unit || "—"}
            />
            <SummaryRow
              label={t("renewal.ntv.leaseEndLabel")}
              value={formatDisplayDate(data.leaseEndDate)}
            />
          </Box>
        </>
      )}

      {step === "details" && (
        <>
          <Typography component="h1" sx={headingSx}>
            {t("renewal.ntv.detailsTitle")}
          </Typography>
          <Typography sx={{ mb: 3, color: tokens.colors.inkSecondary }}>
            {t("renewal.ntv.detailsSubtitle")}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <TextField
              label={t("renewal.ntv.moveOutDate")}
              type="date"
              value={draft.moveOutDate}
              onChange={(e) =>
                setDraft((d) => ({ ...d, moveOutDate: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: minDate }}
              helperText={t("renewal.ntv.noticeHint", { days: noticeDays })}
              fullWidth
            />

            <FormControl>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>
                {t("renewal.ntv.allLeaving")}
              </Typography>
              <RadioGroup
                row
                value={
                  draft.allLeaving === null
                    ? ""
                    : draft.allLeaving
                    ? "yes"
                    : "no"
                }
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    allLeaving: e.target.value === "yes",
                    personIds: e.target.value === "yes" ? [] : d.personIds,
                  }))
                }
              >
                <FormControlLabel
                  value="yes"
                  control={<Radio />}
                  label={t("renewal.ntv.allLeavingYes")}
                />
                <FormControlLabel
                  value="no"
                  control={<Radio />}
                  label={t("renewal.ntv.allLeavingNo")}
                />
              </RadioGroup>
            </FormControl>

            {draft.allLeaving === false && (
              <Box>
                <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                  {t("renewal.ntv.whoMoving")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mb: 1, color: tokens.colors.inkMuted }}
                >
                  {t("renewal.ntv.whoMovingHint")}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {household.map((member) => (
                    <FormControlLabel
                      key={member.id}
                      control={
                        <Checkbox
                          checked={draft.personIds.includes(member.id)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setDraft((d) => ({
                              ...d,
                              personIds: checked
                                ? [...d.personIds, member.id]
                                : d.personIds.filter((id) => id !== member.id),
                            }));
                          }}
                        />
                      }
                      label={member.name}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <FormControl fullWidth>
              <InputLabel id="ntv-reason-label">
                {t("renewal.ntv.reason")}
              </InputLabel>
              <Select
                labelId="ntv-reason-label"
                label={t("renewal.ntv.reason")}
                value={draft.reasonId}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    reasonId: String(e.target.value),
                  }))
                }
                displayEmpty
              >
                <MenuItem value="" disabled>
                  {t("renewal.ntv.reasonPlaceholder")}
                </MenuItem>
                {reasons.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label={t("renewal.ntv.forwarding")}
              value={draft.forwardingAddress}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  forwardingAddress: e.target.value,
                }))
              }
              placeholder={t("renewal.ntv.forwardingPlaceholder")}
              helperText={t("renewal.ntv.forwardingHint")}
              multiline
              minRows={2}
              fullWidth
              required
            />

            {fieldError && (
              <FormHelperText error sx={{ mx: 0, typography: "body2" }}>
                {fieldError}
              </FormHelperText>
            )}
          </Box>
        </>
      )}

      {(step === "review" || step === "done") && (
        <>
          <Typography component="h1" sx={headingSx}>
            {step === "done"
              ? t("renewal.ntv.successTitle")
              : t("renewal.ntv.reviewTitle")}
          </Typography>
          <Typography sx={{ mb: 3, color: tokens.colors.inkSecondary }}>
            {step === "done"
              ? t("renewal.ntv.successBody")
              : t("renewal.ntv.reviewSubtitle")}
          </Typography>

          {step === "done" && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate("/home")}
              sx={{ minWidth: 160 }}
            >
              {t("renewal.stub.backHome")}
            </Button>
          )}

          {step === "review" && (
            <Box
              sx={{
                border: `1px solid ${tokens.colors.border}`,
                borderRadius: `${tokens.radius.md}px`,
                px: 2,
                backgroundColor: tokens.colors.surface,
                mb: 2,
              }}
            >
              <SummaryRow
                label={t("renewal.ntv.propertyLabel")}
                value={`${data.propertyName || "—"} · ${t(
                  "renewal.ntv.unitLabel"
                )} ${data.unit || "—"}`}
              />
              <SummaryRow
                label={t("renewal.ntv.moveOutDate")}
                value={formatDisplayDate(draft.moveOutDate)}
              />
              <SummaryRow
                label={t("renewal.ntv.allLeaving")}
                value={
                  draft.allLeaving
                    ? t("renewal.ntv.allLeavingYes")
                    : t("renewal.ntv.allLeavingNo")
                }
              />
              <SummaryRow
                label={t("renewal.ntv.whoMoving")}
                value={leavingNames || "—"}
              />
              <SummaryRow
                label={t("renewal.ntv.reason")}
                value={reasonLabel || "—"}
              />
              <SummaryRow
                label={t("renewal.ntv.forwarding")}
                value={draft.forwardingAddress.trim() || "—"}
              />
            </Box>
          )}

          {submitError && (
            <FormHelperText error sx={{ mx: 0, typography: "body2" }}>
              {submitError}
            </FormHelperText>
          )}
        </>
      )}
    </RenewalWizardShell>
  );
};
